import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { useDivisionOptions, useTeamOptions } from '../../../state/master';
import { getDepartmentScopeWarning, parseLegacyPublicScopeTargets, resolvePublicScopeTargets } from '../../../lib/weeklyReportScopeTargets';
import { normalizeWeeklyReportPublicScopeForSave } from './weeklyReportPublicScopePayload';

type PublicScopeValue = {
  department_id?: number | null;
  scope_department_id?: number | null;
  scope_warning?: string | null;
  stored_scope?: { department_ids?: unknown; team_ids?: unknown; public_department?: unknown; public_team?: unknown } | null;
  scope_type: 'public' | 'teams' | 'department' | 'departments';
  department_ids: number[];
  team_ids: number[];
  private_flg: number;
  public_department: string | null;
  public_team: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  targetLabel?: string;
  targetKey?: string;
  targetColor?: string;
  departmentId: number;
  teamId: number;
  initialPublic?: Partial<PublicScopeValue> | null;
  onApply?: (next: PublicScopeValue) => void;
};

type PublishScopeMode = 'team' | 'department' | 'selectedDepartments' | 'public';

type DepartmentOption = {
  id: string;
  code: string;
  label: string;
  disabled?: boolean;
};

type TeamOption = DepartmentOption & {
  departmentId: string;
};

const getItemId = (item: any, idKeys: string[]) => {
  for (const key of idKeys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') return String(value);
  }
  return '';
};

const getItemCode = (item: any, fallback = '') =>
  String(item?.value ?? item?.code ?? fallback).trim();

const getItemLabel = (item: any, fallback = '') =>
  String(item?.label ?? item?.name ?? item?.value ?? fallback).trim();

const formatOrgName = (item: any, fallback: string) => {
  if (!item) return fallback;
  const code = getItemCode(item);
  const label = getItemLabel(item);
  if (code && label && code !== label) return `${code} ${label}`;
  return label || code || fallback;
};

const WeeklyReportPublishScopeDialog: React.FC<Props> = ({
  open,
  onClose,
  targetLabel,
  targetKey,
  targetColor,
  departmentId,
  teamId,
  initialPublic,
  onApply,
}) => {
  const theme = useTheme();
  const {
    divisionOptions, divisionOptionsRaw,
    isLoading: departmentsLoading, error: departmentsError, refreshDivisionOptions,
  } = useDivisionOptions();
  const {
    teamOptions, teamOptionsRaw,
    isLoading: teamsLoading, error: teamsError, refreshTeamOptions,
  } = useTeamOptions();
  const departmentsMaster = divisionOptionsRaw ?? divisionOptions;
  const teamsMaster = teamOptionsRaw ?? teamOptions;
  const [mode, setMode] = React.useState<PublishScopeMode>('public');
  const [selectedTeamIds, setSelectedTeamIds] = React.useState<string[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = React.useState<string[]>([]);
  const [unresolvedTeams, setUnresolvedTeams] = React.useState<string[]>([]);
  const [unresolvedDepartments, setUnresolvedDepartments] = React.useState<string[]>([]);
  const [applyError, setApplyError] = React.useState<string | null>(null);
  const unresolvedTargets = mode === 'team' ? unresolvedTeams
    : mode === 'selectedDepartments' ? unresolvedDepartments : [];
  const selectionEdited = React.useRef(false);
  const normalizedTargetColor = String(targetColor ?? '').trim();
  const targetLabelBackground = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalizedTargetColor)
    ? normalizedTargetColor
    : theme.palette.warning.dark;

  const departmentOptionsSnapshot = JSON.stringify(
    (Array.isArray(departmentsMaster) ? departmentsMaster : []).map((item: any) => {
      const id = getItemId(item, ['department_id', 'id']) || getItemCode(item);
      return {
        id,
        code: getItemCode(item, id),
        label: getItemLabel(item, id),
        disabled: String(item?.status ?? '').toUpperCase() === 'N',
      };
    })
  );

  const departmentOptions = React.useMemo<DepartmentOption[]>(() => {
    const seen = new Set<string>();
    const options = JSON.parse(departmentOptionsSnapshot) as DepartmentOption[];
    return options.filter((item) => {
      if (!item.id || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [departmentOptionsSnapshot]);

  const currentDepartment = React.useMemo(
    () => (Array.isArray(departmentsMaster) ? departmentsMaster : []).find(
      (item: any) => Number(getItemId(item, ['department_id', 'id'])) === Number(departmentId)
    ),
    [departmentId, departmentsMaster]
  );

  const currentDepartmentCode = getItemCode(currentDepartment, String(departmentId || ''));
  const departmentScopeWarning = getDepartmentScopeWarning(initialPublic, departmentId, departmentOptions);
  const currentDepartmentLabel = getItemLabel(currentDepartment, currentDepartmentCode);
  const departmentName = formatOrgName(currentDepartment, '選択中の課');

  const teamOptionsSnapshot = JSON.stringify(
    (Array.isArray(teamsMaster) ? teamsMaster : []).map((item: any) => {
      const id = getItemId(item, ['team_id', 'id']) || getItemCode(item);
      return {
        id,
        code: getItemCode(item, id),
        label: getItemLabel(item, id),
        disabled: String(item?.status ?? '').toUpperCase() === 'N',
        departmentId: getItemId(item, ['department_id', 'departmentId', 'department']),
      };
    })
  );

  const availableTeamOptions = React.useMemo<TeamOption[]>(() => {
    const seen = new Set<string>();
    const options = JSON.parse(teamOptionsSnapshot) as TeamOption[];
    const uniqueOptions = options.filter((item) => {
      if (!item.id || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
    const hasDepartmentInformation = uniqueOptions.some((item) => item.departmentId);
    if (!hasDepartmentInformation) return uniqueOptions;
    const currentDepartmentKeys = new Set(
      [String(departmentId || ''), currentDepartmentCode, currentDepartmentLabel].filter(Boolean)
    );
    return uniqueOptions.filter((item) => currentDepartmentKeys.has(item.departmentId));
  }, [teamOptionsSnapshot, departmentId, currentDepartmentCode, currentDepartmentLabel]);

  React.useEffect(() => {
    selectionEdited.current = false;
    setApplyError(null);
  }, [open, targetKey, departmentId, teamId]);

  React.useEffect(() => {
    if (!open || selectionEdited.current) return;

    const teams = resolvePublicScopeTargets(initialPublic?.team_ids, initialPublic?.public_team, availableTeamOptions);
    const departments = resolvePublicScopeTargets(initialPublic?.department_ids, initialPublic?.public_department, departmentOptions);
    const privateFlag = Number(initialPublic?.private_flg);
    const explicitScope = String(initialPublic?.scope_type ?? '').trim().toLowerCase();
    const hasTeamTarget = teams.ids.length > 0 || parseLegacyPublicScopeTargets(initialPublic?.public_team).length > 0;
    const scopeType = explicitScope || (privateFlag === 2 ? 'departments' : privateFlag === 1 ? (hasTeamTarget ? 'teams' : 'department') : 'public');
    setUnresolvedTeams(teams.unresolved);
    setUnresolvedDepartments(departments.unresolved);
    if (scopeType === 'teams') {
      setMode('team');
      setSelectedTeamIds(teams.ids);
      setSelectedDepartmentIds([]);
    } else if (scopeType === 'department') {
      setMode('department');
      setSelectedTeamIds([]);
      setSelectedDepartmentIds([String(departmentId)]);
    } else if (scopeType === 'departments') {
      setMode('selectedDepartments');
      setSelectedTeamIds([]);
      setSelectedDepartmentIds(departments.ids);
    } else {
      setMode('public');
      setSelectedTeamIds([]);
      setSelectedDepartmentIds([]);
    }
  }, [open, initialPublic, departmentId, teamId, targetKey, availableTeamOptions, departmentOptions]);

  const masterLoading = mode === 'team'
    ? teamsLoading && availableTeamOptions.length === 0
    : (mode === 'selectedDepartments' || mode === 'department') &&
      departmentsLoading && departmentOptions.length === 0;
  const selectedTeamsMissing = mode === 'team' && selectedTeamIds.length === 0;
  const selectedDepartmentsMissing = mode === 'selectedDepartments' && selectedDepartmentIds.length === 0;
  const selectionMissing = masterLoading || selectedTeamsMissing || selectedDepartmentsMissing ||
    ((mode === 'team' || mode === 'selectedDepartments') && unresolvedTargets.length > 0);

  const buildPublicValue = (): PublicScopeValue => {
    if (mode === 'team') {
      const selectedCodes = selectedTeamIds.map((id) => availableTeamOptions.find((option) => option.id === id)?.code ?? id);
      return {
        scope_type: 'teams',
        department_ids: [],
        team_ids: selectedTeamIds.map(Number).filter(Number.isFinite),
        private_flg: 1,
        public_department: null,
        public_team: selectedCodes.length > 0 ? selectedCodes.join(',') : null,
      };
    }

    if (mode === 'department') {
      return {
        scope_type: 'department',
        department_ids: Number.isFinite(Number(departmentId)) ? [Number(departmentId)] : [],
        team_ids: [],
        private_flg: 1,
        public_department: currentDepartmentCode || null,
        public_team: null,
      };
    }

    if (mode === 'selectedDepartments') {
      const selectedCodes = selectedDepartmentIds.map((id) => departmentOptions.find((option) => option.id === id)?.code ?? id);
      return {
        scope_type: 'departments',
        department_ids: selectedDepartmentIds.map(Number).filter(Number.isFinite),
        team_ids: [],
        private_flg: 2,
        public_department: selectedCodes.length > 0 ? selectedCodes.join(',') : null,
        public_team: null,
      };
    }

    return {
      scope_type: 'public',
      department_ids: [],
      team_ids: [],
      private_flg: 0,
      public_department: null,
      public_team: null,
    };
  };

  const handleApply = () => {
    if (selectionMissing) return;
    setApplyError(null);
    try {
      // Use the same validation for applying the draft and saving the parent.
      const value = normalizeWeeklyReportPublicScopeForSave(buildPublicValue(), {
        departmentId,
        teamId,
        divisionOptions: departmentsMaster,
        teamOptions: teamsMaster,
      });
      onApply?.(value);
      onClose();
    } catch (error) {
      setApplyError(error instanceof Error ? error.message : '公開先を確認してください。');
    }
  };

  const changeMode = (nextMode: PublishScopeMode) => {
    selectionEdited.current = true;
    setApplyError(null);
    setMode(nextMode);
  };

  const activateScopeBeforeOpen = (
    event: React.SyntheticEvent,
    targetMode: Extract<PublishScopeMode, 'team' | 'selectedDepartments'>
  ) => {
    if (mode === targetMode) return;
    event.preventDefault();
    event.stopPropagation();
    changeMode(targetMode);
  };

  const activateScopeBeforeKeyboardOpen = (
    event: React.KeyboardEvent,
    targetMode: Extract<PublishScopeMode, 'team' | 'selectedDepartments'>
  ) => {
    if (!['Enter', ' ', 'ArrowDown'].includes(event.key)) return;
    activateScopeBeforeOpen(event, targetMode);
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
      fullWidth
      maxWidth={false}
      aria-labelledby="weekly-report-publish-scope-title"
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 32px)', sm: 640 },
          maxWidth: 'calc(100% - 32px)',
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', pr: 3, pt: 2 }}>
          <Box
            data-testid="weekly-report-publish-scope-color-label"
            style={{ backgroundColor: targetLabelBackground, color: '#fff' }}
            sx={{
              color: '#fff !important',
              px: 3,
              py: 1,
              minWidth: { xs: 180, sm: 220 },
              fontWeight: 700,
              fontSize: '1rem',
            }}
          >
            {targetLabel || '週報分類'}
          </Box>
        </Box>
        <Box sx={{ px: 3, pt: 2.5, pb: 0.5 }}>
          <Typography id="weekly-report-publish-scope-title" variant="h6" component="h2" fontWeight={700}>
            公開範囲設定
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            この週報分類を閲覧できる範囲を選択してください。
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1.5, pb: 1 }}>
        {departmentScopeWarning && <Alert severity="warning" sx={{ mb: 1 }}>{departmentScopeWarning}</Alert>}
        <Alert severity="info" sx={{ mb: 1 }}>
          「設定する」は編集中の画面への反映です。保存するには、戻った画面で「更新する」を押してください。
        </Alert>
        {masterLoading && <Alert severity="info" sx={{ mb: 1 }}>公開先の一覧を読み込んでいます。</Alert>}
        {(departmentsError || teamsError) && (
          <Alert
            severity="warning"
            sx={{ mb: 1 }}
            action={
              <Button size="small" onClick={() => {
                if (departmentsError) refreshDivisionOptions();
                else if (teamsError) refreshTeamOptions();
              }}>
                再取得
              </Button>
            }
          >
            公開先の一覧を取得できませんでした。編集中の選択は保持しています。
          </Alert>
        )}
        {applyError && <Alert severity="error" sx={{ mb: 1 }}>{applyError}</Alert>}
        {unresolvedTargets.length > 0 && (mode === 'team' || mode === 'selectedDepartments') && (
          <Alert severity="error" sx={{ mb: 1 }}>
            復元できない公開先があります（{unresolvedTargets.join('、')}）。公開先を選び直してください。
          </Alert>
        )}
        <RadioGroup
          name="weekly-report-publish-scope"
          value={mode}
          onChange={(event) => changeMode(event.target.value as PublishScopeMode)}
          sx={{ gap: 1 }}
        >
          <FormControlLabel
            value="public"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={700}>公開</Typography>
                <Typography variant="caption" color="text.secondary">週報アプリユーザー全員に公開</Typography>
              </Box>
            }
            sx={scopeOptionSx(mode === 'public')}
          />

          <FormControlLabel
            value="department"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={700}>課内公開</Typography>
                <Typography variant="caption" color="text.secondary">{departmentName}のメンバーのみ公開</Typography>
              </Box>
            }
            sx={scopeOptionSx(mode === 'department')}
          />

          <Box sx={scopeOptionWithControlSx(mode === 'selectedDepartments')}>
            <FormControlLabel
              value="selectedDepartments"
              control={<Radio />}
              label={<Typography variant="body2" fontWeight={700}>指定課に公開</Typography>}
              sx={{ m: 0, minWidth: 0, alignSelf: 'stretch' }}
            />
            <FormControl
              size="small"
              error={selectedDepartmentsMissing}
              onMouseDownCapture={(event) => activateScopeBeforeOpen(event, 'selectedDepartments')}
              onKeyDownCapture={(event) => activateScopeBeforeKeyboardOpen(event, 'selectedDepartments')}
              sx={scopeControlSx}
            >
              <Select
                multiple
                displayEmpty
                value={selectedDepartmentIds}
                onChange={(event) => {
                  const value = event.target.value;
                  const nextDepartmentIds = typeof value === 'string' ? value.split(',') : value as string[];
                  selectionEdited.current = true;
                  setUnresolvedDepartments([]);
                  setApplyError(null);
                  setSelectedDepartmentIds(nextDepartmentIds);

                  // ラジオを切り替えただけでは元の値を残し、実際に課を選択した時点で
                  // 排他的な「チーム内公開」の選択を画面上の一時状態から消す。
                  if (nextDepartmentIds.length > 0) setSelectedTeamIds([]);
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) return <span style={{ color: '#8a8a8a' }}>-- 課を選択 --</span>;
                  return selected
                    .map((id) => departmentOptions.find((option) => option.id === id)?.label ?? `登録済みの課（ID: ${id}）`)
                    .join('、');
                }}
                inputProps={{ 'aria-label': '公開する課' }}
              >
                {departmentOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id} disabled={option.disabled}>
                    <Checkbox checked={selectedDepartmentIds.includes(option.id)} />
                    <ListItemText primary={option.label} secondary={option.code !== option.label ? option.code : undefined} />
                  </MenuItem>
                ))}
              </Select>
              {selectedDepartmentsMissing && <FormHelperText>公開する課を選択してください</FormHelperText>}
            </FormControl>
          </Box>

          <Box sx={scopeOptionWithControlSx(mode === 'team')}>
            <FormControlLabel
              value="team"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700}>チーム内公開</Typography>
                  <Typography variant="caption" color="text.secondary">選択したチームのメンバーのみ公開</Typography>
                </Box>
              }
              sx={{ m: 0, minWidth: 0, alignSelf: 'stretch' }}
            />
            <FormControl
              size="small"
              error={selectedTeamsMissing}
              onMouseDownCapture={(event) => activateScopeBeforeOpen(event, 'team')}
              onKeyDownCapture={(event) => activateScopeBeforeKeyboardOpen(event, 'team')}
              sx={scopeControlSx}
            >
              <Select
                multiple
                displayEmpty
                value={selectedTeamIds}
                onChange={(event) => {
                  const value = event.target.value;
                  const nextTeamIds = typeof value === 'string' ? value.split(',') : value as string[];
                  selectionEdited.current = true;
                  setUnresolvedTeams([]);
                  setApplyError(null);
                  setSelectedTeamIds(nextTeamIds);

                  // ラジオを切り替えただけでは元の値を残し、実際にチームを選択した時点で
                  // 排他的な「指定課に公開」の選択を画面上の一時状態から消す。
                  if (nextTeamIds.length > 0) setSelectedDepartmentIds([]);
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) return <span style={{ color: '#8a8a8a' }}>-- チームを選択 --</span>;
                  return selected.map((id) => {
                    const option = availableTeamOptions.find((candidate) => candidate.id === id);
                    return option ? (option.code === option.label ? option.label : `${option.code} ${option.label}`) : `登録済みのチーム（ID: ${id}）`;
                  })
                    .join('、');
                }}
                inputProps={{ 'aria-label': '公開するチーム' }}
              >
                {availableTeamOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id} disabled={option.disabled}>
                    <Checkbox checked={selectedTeamIds.includes(option.id)} />
                    <ListItemText primary={option.label} secondary={option.code !== option.label ? option.code : undefined} />
                  </MenuItem>
                ))}
              </Select>
              {selectedTeamsMissing && <FormHelperText>公開するチームを選択してください</FormHelperText>}
            </FormControl>
          </Box>

        </RadioGroup>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color="inherit" onClick={onClose}>キャンセル</Button>
        <Button data-testid="weekly-report-publish-scope-apply-button" variant="contained" onClick={handleApply} disabled={selectionMissing}>設定する</Button>
      </DialogActions>
    </Dialog>
  );
};

const scopeOptionSx = (selected: boolean) => ({
  m: 0,
  px: 1.5,
  py: 1,
  border: '1px solid',
  borderColor: selected ? 'primary.main' : 'divider',
  borderRadius: 1.5,
  bgcolor: selected ? 'action.selected' : 'background.paper',
  alignItems: 'center',
  transition: 'border-color 120ms ease, background-color 120ms ease',
});

const scopeOptionWithControlSx = (selected: boolean) => ({
  ...scopeOptionSx(selected),
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '190px minmax(0, 1fr)' },
  columnGap: 1.5,
  rowGap: 0.75,
});

const scopeControlSx = {
  width: '100%',
  minWidth: 0,
  ml: 0,
  mt: { xs: 0.5, sm: 0 },
  '& .MuiInputBase-root': {
    width: '100%',
    minWidth: 0,
  },
  '& .MuiSelect-select': {
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

export default WeeklyReportPublishScopeDialog;
